/// <reference types="react-scripts" />
import Loader from "./../../../../Content/devel/images/large_loader.gif";
import * as React from "react";
import * as calendarTaskService from "../../models/services/calendarTaskService";
import { Translations } from "ui/Scripts/source/models/translations";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Base } from "ui/Scripts/source/framework/base";
import { ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { infiniteListPageHalfSize } from "ui/Scripts/source/models/consts";
import { AppUtils } from "../../framework/appUtils";
import { ICalendarTaskItem } from "ui/Scripts/source/models/calendarTask/calendarTaskItem";
import { useEffect, useState } from "react";
import { CalendarTaskListLine } from "./calendarTaskListLine";
import { CalendarTaskEditDialog } from "./calendarTaskEditDialog";
import { IUserState } from "source/models/store/storeTypes";

//CalendarTaskListOfRecordMenu
interface ICalendarTaskListOfRecordMenuProps {
    item: ICalendarTaskItem;
    onEdit: (id: string, isSeries: boolean) => void;
    onRemove: (id: string, isSeries: boolean) => void;
    onCancel: () => void;
}

export const CalendarTaskListOfRecordMenu = (props: ICalendarTaskListOfRecordMenuProps) => {
    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleEditClick = (e, id: string, isSeries: boolean) => {
        handleClick(e);
        props.onEdit(id, isSeries);
    };

    const handleRemoveClick = (e, id: string, isSeries: boolean) => {
        handleClick(e);
        props.onRemove(id, isSeries);
    };

    const handleCancelClick = (e) => {
        handleClick(e);
        props.onCancel();
    };
        
    return (
        <div className="listPopupMenu" onClick={handleCancelClick}>
            <div className="menu">
                {(!props.item.parentId
                    ? <>
                        <div className="line" onClick={(e) => handleEditClick(e, props.item.id, false)}>{Translations.Edit}</div>
                        <div className="line" onClick={(e) => handleRemoveClick(e, props.item.id, false)}>{Translations.Remove}</div>
                    </>
                    : <>
                        <div className="line" onClick={(e) => handleEditClick(e, props.item.id, false)}>{Translations.CalendarTaskEditOccurrence}</div>
                        <div className="line" onClick={(e) => handleEditClick(e, props.item.parentId, true)}>{Translations.CalendarTaskEditSeries}</div>
                        <div className="line" onClick={(e) => handleRemoveClick(e, props.item.id, false)}>{Translations.CalendarTaskRemoveOccurrence}</div>
                        <div className="line" onClick={(e) => handleRemoveClick(e, props.item.parentId, true)}>{Translations.CalendarTaskRemoveSeries}</div>
                    </>
                )}
                <div className="line" onClick={handleCancelClick}>{Translations.Cancel}</div>
            </div>
        </div>
    );
};

//CalendarTaskListOfRecord
//***************************************************************
export interface ICalendarTaskListOfRecordProp {
    user: IUserState;
    isReadOnly: boolean;
    databaseRecordType: DatabaseRecordType;
    recordId: string;
    newRecordNotification: string;
    accordionIsVisible: boolean;
    onChangeAccordionIsVisible: (accordionIsVisible: boolean) => void;
}

interface ICalendarTaskListOfRecordState {
    isLoading: boolean;
    page: number;
    hasMore: boolean;
    totalItemCount: number;
    items: ICalendarTaskItem[];
    selectedId: string;
    showListMenu: boolean;
    showEditDialog: boolean;
    editDialogId: string;
    editDialogHasPrevious: boolean;
    editDialogHasNext: boolean;
}

export const CalendarTaskListOfRecord = (props: ICalendarTaskListOfRecordProp) => {
    const [state, setState] = useState<ICalendarTaskListOfRecordState>({
        isLoading: false,
        page: 1,
        hasMore: true,
        totalItemCount: 0,
        items: [],
        selectedId: "",
        showListMenu: false,
        showEditDialog: false,
        editDialogId: "",
        editDialogHasPrevious: false,
        editDialogHasNext: false
    });

    const searchItems = async (page: number, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        const isNewRecord = props.recordId === Base.emptyGuid;
        if (isNewRecord) {
            setState((old) => ({ ...old, intialized: true }));
            return;
        }
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskListItems(props.databaseRecordType, props.recordId, !refreshList ? infiniteListPageHalfSize : (page * infiniteListPageHalfSize), !refreshList ? page : 1), false, true, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const items = Base.getListItems(state.items, state.selectedId, [], result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        setState((old) => ({
            ...old,
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            totalItemCount: result.totalItemCount,
            items: items.items,
            selectedId,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        }));
    };
    
    useEffect(() => {
        searchItems(state.page, false, false);
    }, []);

    useEffect(() => {
        searchItems(1, true, false);
    }, [props.recordId]);

    const refreshList = async (setEditDialogData: boolean = true): Promise<void> => {
        await searchItems(state.page, false, true, false, setEditDialogData);
    };

    const handleLoadMore = () => {
        if (!state.hasMore) return;
        searchItems(state.page + 1, false, false);
    };

    // #region Edit dialog
    const showEditDialog = (calendarTaskId: string, isSeries: boolean) => {
        const isNew = calendarTaskId === Base.emptyGuid;
        const index = isNew ? 0 : state.items.findIndex(i => i.id === calendarTaskId);
        if (index < 0) return;
        setState((old) => ({
            ...old,
            showEditDialog: true,
            editDialogId: calendarTaskId,
            editDialogHasPrevious: !isSeries && index > 0,
            editDialogHasNext: isSeries || isNew ? false : index < state.items.length - 1 || state.hasMore
        }));
    };

    const handleAddCalendarTask = () => {
        showEditDialog(Base.emptyGuid, false);
    };

    const handleOpenCalendarTask = (id: string, isSeries: boolean) => {
        showEditDialog(id, isSeries);
    };

    const handleCloseEditDialog = () => {
        setState((old) => ({
            ...old,
            showEditDialog: false,
        }));
        refreshList();
    };

    // const setEditDialogItemByIndex = (index: number) => {
    //     if (index < 0) return;
    //     if (index >= state.items.length) {
    //         if (!state.hasMore) return;
    //         searchItems(state.page + 1, false, false, true);
    //     } else {
    //         setState((old) => ({
    //             ...old,
    //             selectedId: state.items[index].id,
    //             editDialogId: state.items[index].id,
    //             editDialogHasPrevious: index > 0,
    //             editDialogHasNext: index < state.items.length - 1 || state.hasMore,
    //         }));
    //     }
    // };

    // const handleMoveToPrevious = () => {
    //     if (!state.editDialogId) return;
    //     const index = state.items.findIndex(i => i.id === state.selectedId);
    //     if (index < 0) return;
    //     setEditDialogItemByIndex(index - 1);
    // };

    // const handleMoveToNext = () => {
    //     if (!state.editDialogId) return;
    //     const index = state.items.findIndex(i => i.id === state.editDialogId);
    //     if (index < 0) return;
    //     setEditDialogItemByIndex(index + 1);
    // };
    // #endregion Edit dialog

    //# region Remove
    const remove = async (id: string) => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskParameter, state.items?.find(item => item.id === id)?.title), false) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => calendarTaskService.removeCalendarTask(id));
            if (!result) return false;
        }
        refreshList();
        return true;
    };
    //# endregion Remove
    
    const handleLineClick = (selectedId: string) => {
        setState((old) => ({
            ...old,
            selectedId
        }));
        showEditDialog(selectedId, false);
    };

    const handleLineLongPress = (selectedId: string) => {
        setState((old) => ({
            ...old,
            selectedId,
            showListMenu: true
        }));
    };

    const handleMenuRemove = async (id: string, _isSeries: boolean) => {
        if (!id) return;
        if (!remove(id)) return;
        setState((old) => ({
            ...old,
            showListMenu: false,
            showEditDialog: false
        }));
    };

    const handleMenuCancel = () => {
        setState((old) => ({
            ...old,
            showListMenu: false
        }));
    };

    const isNewRecord = props.recordId === Base.emptyGuid;

    return (
        <>
            <div className="sectionTitle">{Translations.CalendarTasks + " (" + state.totalItemCount + ")"}
                <div className={"btn " + (props.accordionIsVisible ? "collapseUp" : "expandDown")} onClick={() => props.onChangeAccordionIsVisible(!props.accordionIsVisible)}><span className="icon"></span></div>
            </div>
            {props.accordionIsVisible &&
                <>
                    <div className="row">
                        <div className="col-12">
                            <div className="fullLeft">
                                {!props.isReadOnly && !isNewRecord &&
                                    <ToolButton
                                        tooltip={Translations.Add}
                                        classes="round right mono add"
                                        onClick={handleAddCalendarTask}/>
                                }
                            </div>
                            {isNewRecord &&
                                <div className="fullLeft">
                                    <div className="alert alert-info" role="alert">{props.newRecordNotification}</div>
                                </div>
                            }
                            {state.items.length > 0 &&
                                <div className="fullLeft lineContainer calendarTasks">
                                    {state.items.map((calendarTask) => (
                                        <CalendarTaskListLine
                                            item={calendarTask}
                                            first={false}
                                            isLastOpenedEvent={false}
                                            key={calendarTask.id}
                                            hideDate={true}
                                            recordListMode={true}
                                            selected={calendarTask.id === state.selectedId}
                                            onClick={handleLineClick}
                                            onLongPress={!props.isReadOnly ? handleLineLongPress : null}
                                            onRefreshList={null}
                                            onMoveToNextWeek={null}
                                            onMoveToPreviousWeek={null}
                                        />
                                    ))}
                                    {state.hasMore &&
                                        <div className="loadMoreLink" onClick={handleLoadMore}>{Translations.LoadMore}</div>
                                    }
                                </div>
                            }
                            {state.isLoading &&
                                <img className="loadingLocal" alt={Translations.Loading} src={Loader} />
                            }
                            {state.showListMenu &&
                                <CalendarTaskListOfRecordMenu
                                    item={state.items.find(i => i.id === state.selectedId)}
                                    onEdit={handleOpenCalendarTask}
                                    onRemove={handleMenuRemove}
                                    onCancel={handleMenuCancel}
                                />
                            }
                            {state.showEditDialog &&
                                <CalendarTaskEditDialog
                                    user={props.user}
                                    isReadOnly={props.isReadOnly}
                                    databaseRecordType={props.databaseRecordType}
                                    recordId={props.recordId}
                                    calendarTaskId={state.editDialogId}
                                    onRemove={() => handleMenuRemove(state.editDialogId, false)}
                                    onClose={handleCloseEditDialog}
                                />
                            }
                        </div>
                    </div>
                </>
            }
        </>
    );
};